import { useCallback, useEffect, useState } from 'react'
import { useI18n } from '../../i18n'
import { Button } from '../ui/Button'
import { EnterpriseOperationalCard } from '../enterprise-operational/EnterpriseOperationalSection'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import { buildCloudRequestUrl } from './gcpDiscoverySession.mjs'
import { getActiveOrgId } from '../../utils/orgUtils'

type OsInfo = { name?: string | null; version?: string | null }

type VmObservation = {
  id: string
  bindingKey?: string | null
  observedAt?: string | null
  sourceType?: string
  os?: OsInfo | null
  packageCount?: number
}

type SourceRow = {
  id?: string
  sourceType: string
  status?: string
  lastObservedAt?: string | null
  lastError?: string | null
  coverage?: {
    complete?: boolean
    limitations?: string[]
    google?: { httpStatus?: number | null; status?: string | null; reason?: string | null; service?: string | null } | null
  }
  privateEndpoint?: boolean
  permission?: { permission?: string } | null
  connected?: boolean
  collectionEnabled?: boolean
  collectionMode?: 'project' | 'vm' | 'not_opted_in'
  displayState?: string
  googleError?: { httpStatus?: number | null; status?: string | null; reason?: string | null; service?: string | null } | null
  vms?: VmObservation[]
}

type InstanceRow = {
  id: string
  name?: string | null
  location?: string | null
}

type PackageEntry = { name?: string | null; version?: string | null }

type PackageView = {
  os?: OsInfo | null
  packages?: PackageEntry[]
  updates?: PackageEntry[]
  observedAt?: string | null
}

type Props = {
  token: string
  orgId: string
  integrationId: string
}

const API = import.meta.env.VITE_API_URL || '/api'

const STATE_CLASS: Record<string, string> = {
  DATA_RECEIVED: entStyles.badgeStable,
  COLLECTING: entStyles.badgeWarn,
  NO_ACCESS: entStyles.badgeRisk,
  INVENTORY_UNAVAILABLE: entStyles.badgeWarn,
  CONFIGURED_NOT_STARTED: entStyles.badgePressureLow,
}

function formatWhen(value?: string | null) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString()
}

function storedErrorLabel(code: string | null | undefined, t: (key: string, fallback: string) => string) {
  if (!code) return ''
  if (code === 'API_DISABLED') return t('gcpSources.apiDisabled', 'API disabled')
  if (code === 'IAM_PERMISSION_DENIED') return t('gcpSources.iamDenied', 'Missing IAM permission')
  if (code === 'OAUTH_SCOPE_INSUFFICIENT') return t('gcpSources.scopeDenied', 'Missing OAuth scope')
  if (code === 'AUTH') return t('gcpSources.authFailed', 'Authentication failed')
  if (code === 'AGENT_OR_INVENTORY_MISSING') return t('gcpSources.stateInventory', 'Inventory is unavailable')
  if (code === 'NOT_OBSERVED') return t('gcpSources.notObserved', 'Not observed')
  return code
}

function errorLine(row: SourceRow | undefined, t: (key: string, fallback: string) => string) {
  const label = storedErrorLabel(row?.lastError, t)
  const google = row?.googleError
  const parts = [label]
  if (google?.reason) parts.push(google.reason)
  if (google?.service) parts.push(google.service)
  if (google?.httpStatus) parts.push(`HTTP ${google.httpStatus}`)
  if (google?.status && google.status !== row?.lastError) parts.push(google.status)
  return parts.filter(Boolean).join(' · ')
}

function vmIdentity(bindingKey?: string | null) {
  const text = String(bindingKey || '')
  const name = text.match(/instances\/([^/]+)/)?.[1] || ''
  const zone = text.match(/\/(?:locations|zones)\/([^/]+)\//)?.[1] || ''
  return { name, zone }
}

async function readJson(response: Response) {
  return response.json().catch(() => ({}))
}

export default function GcpSourcesPanel({ token, orgId, integrationId }: Props) {
  const { t } = useI18n()
  const [sources, setSources] = useState<SourceRow[]>([])
  const [instances, setInstances] = useState<InstanceRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [resourceId, setResourceId] = useState('')
  const [busy, setBusy] = useState(false)
  const [localCollecting, setLocalCollecting] = useState(false)
  const [confirmProject, setConfirmProject] = useState(false)
  const [packageView, setPackageView] = useState<PackageView | null>(null)
  const [packageFor, setPackageFor] = useState<string | null>(null)

  const load = useCallback(async () => {
    const response = await fetch(buildCloudRequestUrl(API, `/${integrationId}/sources`, orgId), {
      headers: { Authorization: `Bearer ${token}` },
    })
    const body = await readJson(response)
    if (getActiveOrgId() !== orgId) return
    if (!response.ok) {
      setSources([])
      setError(t('gcpSources.unavailable', 'Source status is not available.'))
      return
    }
    setSources(Array.isArray(body.sources) ? body.sources : [])
    setError(null)
  }, [integrationId, orgId, t, token])

  const loadInstances = useCallback(async () => {
    const collected: InstanceRow[] = []
    let cursor = ''
    for (let page = 0; page < 5; page += 1) {
      const query = cursor
        ? `/${integrationId}/resources?kind=INSTANCE&limit=100&cursor=${encodeURIComponent(cursor)}`
        : `/${integrationId}/resources?kind=INSTANCE&limit=100`
      const response = await fetch(buildCloudRequestUrl(API, query, orgId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      const body = await readJson(response)
      if (!response.ok || getActiveOrgId() !== orgId) return
      const items = Array.isArray(body.items) ? body.items : []
      collected.push(...items.filter((item: InstanceRow) => item?.id))
      cursor = typeof body.nextCursor === 'string' ? body.nextCursor : ''
      if (!cursor) break
    }
    setInstances(collected)
  }, [integrationId, orgId, token])

  useEffect(() => {
    let active = true
    void load().catch(() => {
      if (!active) return
      setSources([])
      setError(t('gcpSources.unavailable', 'Source status is not available.'))
    })
    void loadInstances().catch(() => {
      if (active) setInstances([])
    })
    return () => {
      active = false
    }
  }, [load, loadInstances, t])

  async function post(path: string, body?: unknown) {
    setBusy(true)
    setError(null)
    try {
      const response = await fetch(buildCloudRequestUrl(API, path, orgId), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      })
      const payload = await readJson(response)
      if (!response.ok) {
        setError(typeof payload.error === 'string' ? payload.error : t('gcpSources.requestFailed', 'The request did not complete.'))
        return false
      }
      await load()
      return true
    } catch {
      setError(t('gcpSources.requestFailed', 'The request did not complete.'))
      return false
    } finally {
      setBusy(false)
      setLocalCollecting(false)
    }
  }

  async function openPackages(sourceId: string, observationId: string) {
    setBusy(true)
    setError(null)
    try {
      const response = await fetch(buildCloudRequestUrl(API, `/${integrationId}/sources/${sourceId}/observations/${observationId}`, orgId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      const body = await readJson(response)
      if (!response.ok) {
        setPackageView(null)
        setError(t('gcpSources.requestFailed', 'The request did not complete.'))
        return
      }
      setPackageFor(observationId)
      setPackageView(body)
    } catch {
      setError(t('gcpSources.requestFailed', 'The request did not complete.'))
    } finally {
      setBusy(false)
    }
  }

  const os = sources.find((row) => row.sourceType === 'OS_CONFIG')
  const others = sources.filter((row) => row.sourceType !== 'OS_CONFIG')
  const displayState = localCollecting ? 'COLLECTING' : (os?.displayState || 'CONFIGURED_NOT_STARTED')
  const stateLabel: Record<string, string> = {
    CONFIGURED_NOT_STARTED: t('gcpSources.stateConfigured', 'Configured, collection has not started'),
    COLLECTING: t('gcpSources.stateCollecting', 'Collection is running'),
    NO_ACCESS: t('gcpSources.stateNoAccess', 'No access'),
    INVENTORY_UNAVAILABLE: t('gcpSources.stateInventory', 'Inventory is unavailable'),
    DATA_RECEIVED: t('gcpSources.stateReceived', 'Data received'),
  }

  return (
    <section data-testid="gcp-sources" className={entStyles.layer}>
      <h3 className={entStyles.cardTitle}>{t('gcpSources.title', 'Sources')}</h3>
      <p className={entStyles.scopeLine}>{t('gcpSources.intro', 'Compute inventory, OS Config, the host collector, and Kubernetes are separate sources. A missing source is a coverage gap.')}</p>
      {error ? <p>{error}</p> : null}
      <div className={entStyles.standardGrid}>
        {others.map((row) => (
          <EnterpriseOperationalCard key={row.id || row.sourceType} title={row.sourceType}>
            <span className={`${entStyles.badge} ${row.status === 'AVAILABLE' ? entStyles.badgeStable : entStyles.badgePressureLow}`}>
              {row.status === 'NOT_CONNECTED' ? t('gcpSources.notConnected', 'Not connected') : (row.status || t('gcpSources.notConfigured', 'Not configured'))}
            </span>
            <dl className={entStyles.metricRows}>
              <div className={entStyles.metricRow}>
                <dt>{t('gcpSources.observed', 'Last observation')}</dt>
                <dd>{formatWhen(row.lastObservedAt) || t('gcpSources.none', 'None')}</dd>
              </div>
              {row.lastError ? (
                <div className={entStyles.metricRow}>
                  <dt>{t('gcpSources.error', 'Error')}</dt>
                  <dd>{row.lastError}</dd>
                </div>
              ) : null}
            </dl>
            {row.permission?.permission ? <p className={entStyles.scopeLine}>{row.permission.permission}</p> : null}
            {row.privateEndpoint ? <p className={entStyles.scopeLine}>{t('gcpSources.privateEndpoint', 'Private endpoint')}</p> : null}
            {row.id && row.sourceType !== 'COMPUTE_IAM' ? (
              <div className={entStyles.cardActions}>
                <Button type="button" size="small" color="outline" disabled={busy} onClick={() => void post(`/${integrationId}/sources/${row.id}/collect`)}>
                  {t('gcpSources.collect', 'Collect')} {row.sourceType}
                </Button>
              </div>
            ) : null}
          </EnterpriseOperationalCard>
        ))}
      </div>

      <EnterpriseOperationalCard title="OS Config">
        <div data-testid="gcp-os-config">
          <span className={`${entStyles.badge} ${STATE_CLASS[displayState] || entStyles.badgePressureLow}`}>
            {stateLabel[displayState] || displayState}
          </span>
          <dl className={entStyles.metricRows}>
            <div className={entStyles.metricRow}>
              <dt>{t('gcpSources.modeLabel', 'Collection mode')}</dt>
              <dd>
                {os?.collectionMode === 'project'
                  ? t('gcpSources.modeProject', 'Project opt-in')
                  : os?.collectionMode === 'vm'
                    ? t('gcpSources.modeVm', 'One-shot VM')
                    : t('gcpSources.modeOff', 'Not opted in')}
              </dd>
            </div>
            <div className={entStyles.metricRow}>
              <dt>{t('gcpSources.error', 'Error')}</dt>
              <dd>{errorLine(os, t) || t('gcpSources.none', 'None')}</dd>
            </div>
            <div className={entStyles.metricRow}>
              <dt>{t('gcpSources.lastSuccess', 'Last successful observation')}</dt>
              <dd>{formatWhen(os?.lastObservedAt) || t('gcpSources.none', 'None')}</dd>
            </div>
            {os?.coverage?.google?.httpStatus ? (
              <div className={entStyles.metricRow}>
                <dt>{t('gcpSources.httpStatus', 'HTTP')}</dt>
                <dd>{os.coverage.google.httpStatus}</dd>
              </div>
            ) : null}
            {os?.coverage?.google?.status ? (
              <div className={entStyles.metricRow}>
                <dt>{t('gcpSources.googleStatus', 'status')}</dt>
                <dd>{os.coverage.google.status}</dd>
              </div>
            ) : null}
            {os?.coverage?.google?.reason ? (
              <div className={entStyles.metricRow}>
                <dt>{t('gcpSources.googleReason', 'reason')}</dt>
                <dd>{os.coverage.google.reason}</dd>
              </div>
            ) : null}
            {os?.coverage?.google?.service ? (
              <div className={entStyles.metricRow}>
                <dt>{t('gcpSources.googleService', 'service')}</dt>
                <dd>{os.coverage.google.service}</dd>
              </div>
            ) : null}
          </dl>
          <div className={entStyles.cardActions}>
            {!os?.id ? (
              <Button type="button" size="small" color="primary" disabled={busy} onClick={() => void post(`/${integrationId}/sources/os-config`)}>
                {t('gcpSources.configureOsConfig', 'Configure OS Config')}
              </Button>
            ) : null}
            {os?.id ? (
              <Button type="button" size="small" color="outline" disabled={busy} onClick={() => setConfirmProject(true)}>
                {t('gcpSources.enableProject', 'Enable project collection')}
              </Button>
            ) : null}
            {os?.id && os.collectionEnabled ? (
              <Button type="button" size="small" color="outline" disabled={busy} onClick={() => void post(`/${integrationId}/sources/${os.id}/os-config/disable`)}>
                {t('gcpSources.disableProject', 'Turn off project collection')}
              </Button>
            ) : null}
            <Button type="button" size="small" color="outline" disabled={busy} onClick={() => void post(`/${integrationId}/sources/gke`)}>
              {t('gcpSources.enableGke', 'Enable GKE')}
            </Button>
          </div>
          {confirmProject && os?.id ? (
            <div>
              <h4 className={entStyles.cardTitle}>{t('gcpSources.projectConfirmTitle', 'Project collection')}</h4>
              <p className={entStyles.scopeLine}>{t('gcpSources.projectScope', 'Scope is the whole project inventory.')}</p>
              <p className={entStyles.scopeLine}>{t('gcpSources.projectLimit', 'One pass stores at most 50 inventories.')}</p>
              <p className={entStyles.scopeLine}>{t('gcpSources.projectLater', 'After enabling, this source joins later syncs.')}</p>
              <div className={entStyles.cardActions}>
                <Button
                  type="button"
                  size="small"
                  color="primary"
                  disabled={busy}
                  onClick={() => {
                    setConfirmProject(false)
                    setLocalCollecting(true)
                    void post(`/${integrationId}/sources/${os.id}/collect`)
                  }}
                >
                  {t('gcpSources.confirmEnable', 'Enable project collection')}
                </Button>
                <Button type="button" size="small" color="outline" disabled={busy} onClick={() => setConfirmProject(false)}>
                  {t('gcpSources.cancel', 'Cancel')}
                </Button>
              </div>
            </div>
          ) : null}
          {os?.collectionEnabled ? <p className={entStyles.scopeLine}>{t('gcpSources.disableKeeps', 'Observations for this source stay in place.')}</p> : null}
          {os?.id ? (
            <div>
              <label htmlFor="gcp-os-config-vm">
                {t('gcpSources.vmSelector', 'Discovered virtual machine')}
                <select
                  id="gcp-os-config-vm"
                  value={resourceId}
                  onChange={(event) => setResourceId(event.target.value)}
                  style={{ display: 'block', width: '100%', maxWidth: 480, marginTop: 8, padding: '8px 10px', borderRadius: 8 }}
                >
                  <option value="">{t('gcpSources.selectVm', 'Select a virtual machine')}</option>
                  {instances.map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {(instance.name || instance.id) + (instance.location ? ` · ${instance.location}` : '')}
                    </option>
                  ))}
                </select>
              </label>
              {instances.length === 0 ? <p className={entStyles.scopeLine}>{t('gcpSources.noVms', 'No discovered virtual machines for this connection.')}</p> : null}
              <div className={entStyles.cardActions}>
                <Button
                  type="button"
                  size="small"
                  color="primary"
                  disabled={busy || !resourceId}
                  onClick={() => {
                    setLocalCollecting(true)
                    void post(`/${integrationId}/sources/${os.id}/os-config/vm`, { resourceId })
                  }}
                >
                  {t('gcpSources.collectVm', 'Collect for VM')}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </EnterpriseOperationalCard>

      <div className={entStyles.standardGrid}>
        {(os?.vms || []).map((vm) => {
          const identity = vmIdentity(vm.bindingKey)
          const version = [vm.os?.name, vm.os?.version].filter(Boolean).join(' ')
          return (
            <EnterpriseOperationalCard key={vm.id} title={identity.name || t('gcpSources.collectVm', 'Collect for VM')}>
              <dl className={entStyles.metricRows}>
                <div className={entStyles.metricRow}>
                  <dt>{t('gcpSources.osVersion', 'OS version')}</dt>
                  <dd>{version || t('gcpSources.none', 'None')}</dd>
                </div>
                <div className={entStyles.metricRow}>
                  <dt>{t('gcpSources.source', 'Source')}</dt>
                  <dd>{t('gcpSources.observationSource', 'OS Config')}</dd>
                </div>
                <div className={entStyles.metricRow}>
                  <dt>{t('gcpSources.observed', 'Last observation')}</dt>
                  <dd>{formatWhen(vm.observedAt) || t('gcpSources.none', 'None')}</dd>
                </div>
                {identity.zone ? (
                  <div className={entStyles.metricRow}>
                    <dt>{t('gcpSources.vmSelector', 'Discovered virtual machine')}</dt>
                    <dd>{identity.zone}</dd>
                  </div>
                ) : null}
              </dl>
              <div className={entStyles.cardActions}>
                <Button type="button" size="small" color="outline" disabled={busy || !os?.id} onClick={() => void openPackages(os?.id || '', vm.id)}>
                  {t('gcpSources.packages', 'Packages')}
                </Button>
              </div>
              {packageFor === vm.id && packageView ? (
                <div>
                  <h4 className={entStyles.cardTitle}>{t('gcpSources.packages', 'Packages')}</h4>
                  {(packageView.packages || []).length === 0 ? <p className={entStyles.scopeLine}>{t('gcpSources.none', 'None')}</p> : (
                    <ul>
                      {(packageView.packages || []).map((entry) => (
                        <li key={`${entry.name}:${entry.version}`}>{entry.name} {entry.version || ''}</li>
                      ))}
                    </ul>
                  )}
                  <h4 className={entStyles.cardTitle}>{t('gcpSources.updates', 'Available updates')}</h4>
                  {(packageView.updates || []).length === 0 ? <p className={entStyles.scopeLine}>{t('gcpSources.none', 'None')}</p> : (
                    <ul>
                      {(packageView.updates || []).map((entry) => (
                        <li key={`update:${entry.name}:${entry.version}`}>{entry.name} {entry.version || ''}</li>
                      ))}
                    </ul>
                  )}
                  <Button type="button" size="small" color="outline" onClick={() => { setPackageFor(null); setPackageView(null) }}>
                    {t('gcpSources.closePackages', 'Close package list')}
                  </Button>
                </div>
              ) : null}
            </EnterpriseOperationalCard>
          )
        })}
      </div>
      {os?.id && (os.vms || []).length === 0 ? <p className={entStyles.scopeLine}>{t('gcpSources.noVmObservations', 'No VM observation yet.')}</p> : null}
      <p className={entStyles.scopeLine}>{t('gcpSources.install', 'Host collector enrollment, rotation, and the systemd timer are documented with the release notes. The platform does not install the collector.')}</p>
    </section>
  )
}
